// src/managers/BotManager.js
import {
    getFirestore,
    collection,
    getDoc,
    doc,
    setDoc,
    deleteDoc,
    onSnapshot,
    query,
} from 'firebase/firestore';
import { authManager as defaultAuthManager } from '@/managers/AuthManager';
import { keyStateManager as defaultKeyStateManager } from '@/managers/zKeysManager';
import {
    DEFAULT_PROMPTED_BOT_DESCRIPTION,
    DEFAULT_PROMPTED_BOT_ID,
    DEFAULT_PROMPTED_BOT_NAME,
    DEFAULT_PROMPTED_SERVER_BOT_ID,
    DEFAULT_PROMPTED_BOT_SYSTEM_MESSAGE,
    getBotRuntimeSystemMessage,
} from '@/utils/defaultBotProfile';



function dedupePlatformModels(models) {
    const seen = new Set();
    const dedupedModels = [];

    for (const model of models) {
        const dedupeKey = `${String(model.platform || '').toUpperCase()}:${model.model}:${model.id}`;
        if (seen.has(dedupeKey)) {
            continue;
        }
        seen.add(dedupeKey);
        dedupedModels.push(model);
    }

    return dedupedModels;
}

function normalizeRemoteModel(platform, model) {
    if (typeof model !== 'string') return model;
    return model.trim();
}

function getRemoteModelDisplayName(platform, model, fallbackName) {
    const normalizedModel = normalizeRemoteModel(platform, model);
    if (typeof fallbackName === 'string' && fallbackName.trim() && fallbackName.trim() !== model) {
        return fallbackName;
    }
    return fallbackName || normalizedModel;
}

const LOCAL_STORAGE_LAST_BOT_ID_KEY = 'APP_LAST_USED_BOT_ID';
const LOCAL_STORAGE_LAST_MODEL_ID_KEY = 'APP_LAST_USED_MODEL_ID';

const DEFAULT_PROMPTED_BOT_CONFIG = {
    id: DEFAULT_PROMPTED_BOT_ID,
    name: DEFAULT_PROMPTED_BOT_NAME,
    serverBotId: DEFAULT_PROMPTED_SERVER_BOT_ID,
    isHouseBot: true,
    serverPrompted: true,
    description: DEFAULT_PROMPTED_BOT_DESCRIPTION,
    customSystemMessage: DEFAULT_PROMPTED_BOT_SYSTEM_MESSAGE,
    defaultModelId: null,
    variations: [],
};

const VANILLA_BOT_CONFIG = {
    id: 'vanillabot',
    name: 'VanillaBot',
    serverBotId: 'vanillabot',
    isHouseBot: false,
    serverPrompted: false,
    customSystemMessage: null,
    description: 'Direct interaction with a raw AI model.',
    defaultModelId: null,
    variations: [],
};

class BotManagerClass {
    constructor() {
        this._state = {
            isInitialized: false,
            isLoading: true,
            allPlatformModels: [],
            platformModelsWithKeys: [],
            houseBots: [JSON.parse(JSON.stringify(DEFAULT_PROMPTED_BOT_CONFIG))],
            userCustomBots: [],
            currentBotId: null,
            currentModelId: null,
            activeBotConfig: null,
            _userTier: null,
        };
        this._listeners = new Set();
        this._authUnsubscribe = null;
        this._customBotsUnsubscribe = null;
        this._customEndpointsUnsubscribe = null;
        this._settingsUnsubscribe = null;
        this._keyManagerUnsubscribe = null;
        this._authManagerInstance = null;
        this._keyManagerInstance = null;
        this.firestore = null;
        this._hasInitialized = false;
        this._currentUserId = null;
        this._customEndpointModels = [];
    }

    _notifyListeners = () => {
        for (const listener of this._listeners) {
            try {
                listener(this.getState());
            } catch (e) {
                // silent
            }
        }
    }

    _updateLoadingState = (isLoading) => {
        if (this._state.isLoading !== isLoading) {
            this._state.isLoading = isLoading;
            this._notifyListeners();
        }
    }

    _getPlatformsWithKeysSafely = () => {
        if (this._keyManagerInstance && typeof this._keyManagerInstance.getState === 'function') {
            try {
                const keyState = this._keyManagerInstance.getState();
                const activePlatforms = [];
                if (keyState && keyState.keys && typeof keyState.keys === 'object') {
                    for (const platformName in keyState.keys) {
                        if (platformName !== 'timestamp' && keyState.keys[platformName]) {
                            activePlatforms.push(platformName.toUpperCase());
                        }
                    }
                }
                return activePlatforms;
            } catch (e) {
                /* Silent error handling */
            }
        }
        return [];
    }

    _getAvailablePlatformsSafely = () => {
        return this._getPlatformsWithKeysSafely();
    }

    _getTierFilteredModels = (models) => {
        if (this._state._userTier !== 'Free') {
            return models;
        }

        // Firebase is the source of truth. If a model defines free-tier rules,
        // respect them. If no free-tier metadata exists, keep it available.
        return models.filter((model) => {
            const allowedTiers = Array.isArray(model.allowedTiers) ? model.allowedTiers : null;
            const tiers = Array.isArray(model.tiers) ? model.tiers : null;

            if (allowedTiers && allowedTiers.length > 0) {
                return allowedTiers.map(String).some((tier) => tier.toUpperCase() === 'FREE');
            }

            if (tiers && tiers.length > 0) {
                return tiers.map(String).some((tier) => tier.toUpperCase() === 'FREE');
            }

            if (model.freeTier === true) {
                return true;
            }

            if (typeof model.category === 'string' && model.category.toUpperCase() === 'FREE') {
                return true;
            }

            return true;
        });
    }

    _sortModelsForDefaultSelection = (models) => {
        return [...models].sort((a, b) => {
            const orderA = a.order !== undefined ? a.order : Infinity;
            const orderB = b.order !== undefined ? b.order : Infinity;
            if (orderA !== orderB) return orderA - orderB;

            const nameA = a.name || a.cleanModelName || a.model || '';
            const nameB = b.name || b.cleanModelName || b.model || '';
            return nameA.localeCompare(nameB);
        });
    }

    _pickDefaultModel = (models) => {
        if (!Array.isArray(models) || models.length === 0) return null;

        const explicitDefaults = models.filter((model) =>
            model.isDefault === true ||
            model.defaultModel === true ||
            model.defaultForBots === true ||
            model.defaultForShelby === true
        );

        const source = explicitDefaults.length > 0 ? explicitDefaults : models;
        return this._sortModelsForDefaultSelection(source)[0] || null;
    }

    _updatePlatformModelsWithKeys = () => {
        const platformsWithKeysArray = this._getAvailablePlatformsSafely();
        const platformsWithKeysSet = new Set(platformsWithKeysArray);

        // Merge Firestore platform models with user custom endpoint models.
        const allModels = [...this._state.allPlatformModels, ...this._customEndpointModels];
        const tierFilteredModels = this._getTierFilteredModels(allModels);

        this._state.platformModelsWithKeys = tierFilteredModels.filter(model =>
            platformsWithKeysSet.has(String(model.platform).toUpperCase())
        );
    }

    _fetchPlatformModels = async () => {
        try {
            const modelsDocRef = doc(this.firestore, 'platformsettings', 'aimodels');
            const docSnap = await getDoc(modelsDocRef);

            let parsedModels = [];

            if (docSnap.exists()) {
                const docData = docSnap.data();

                if (typeof docData === 'object' && docData !== null && docData.models) {
                    Object.keys(docData.models).forEach(platformKey => {
                        const platformModelsObject = docData.models[platformKey];
                        if (typeof platformModelsObject === 'object' && platformModelsObject !== null) {
                            Object.keys(platformModelsObject).forEach(modelKey => {
                                const modelData = platformModelsObject[modelKey];
                                if (typeof modelData === 'object' && modelData !== null) {
                                    const normalizedPlatform = platformKey.toUpperCase();
                                    const normalizedServerModel = normalizeRemoteModel(normalizedPlatform, modelData.model || modelKey);
                                    const displayName = getRemoteModelDisplayName(normalizedPlatform, modelData.model || modelKey, modelData.cleanModelName || modelKey);
                                    const canonicalModelId = `${platformKey.toLowerCase()}-${modelKey}`;

                                    parsedModels.push({
                                        ...modelData,
                                        id: canonicalModelId,
                                        name: displayName,
                                        platform: normalizedPlatform,
                                        hasThinking: modelData.hasThinking || false,
                                        model: normalizedServerModel,
                                        cleanModelName: displayName,
                                    });
                                }
                            });
                        }
                    });
                }
                this._state.allPlatformModels = dedupePlatformModels(parsedModels);
            } else {
                this._state.allPlatformModels = [];
            }
        } catch (error) {
            this._state.allPlatformModels = [];
        }
    }

    _listenToCustomBots = (userId) => {
        if (this._customBotsUnsubscribe) this._customBotsUnsubscribe();
        try {
            const customBotsPath = `users/${userId}/customBots`;
            const q = query(collection(this.firestore, customBotsPath));
            this._customBotsUnsubscribe = onSnapshot(q, (snapshot) => {
                const tierFilteredAllModels = this._getTierFilteredModels(this._state.allPlatformModels);
                this._state.userCustomBots = snapshot.docs.map(docSnap => {
                    const data = docSnap.data();
                    const defaultModel = this._pickDefaultModel(tierFilteredAllModels);
                    return {
                        id: docSnap.id,
                        name: data.name,
                        description: data.description || '',
                        customSystemMessage: data.customSystemMessage,
                        isCustomBot: true,
                        serverBotId: 'vanillabot',
                        defaultModelId: VANILLA_BOT_CONFIG.defaultModelId || defaultModel?.id
                    };
                });
                if (this._state.currentBotId && !this.getBotById(this._state.currentBotId)) {
                    this._setDefaultBotAndModel();
                } else if (this._state.currentBotId && this._state.userCustomBots.some(cb => cb.id === this._state.currentBotId)) {
                    this._resolveActiveBotConfig();
                }
                this._notifyListeners();
            }, (error) => {
                this._state.userCustomBots = [];
                this._notifyListeners();
            });
        } catch (error) {
            this._state.userCustomBots = [];
            this._notifyListeners();
        }
    }

    _clearCustomBots = () => {
        if (this._customBotsUnsubscribe) {
            this._customBotsUnsubscribe();
            this._customBotsUnsubscribe = null;
        }
        this._state.userCustomBots = [];
    }

    _listenToCustomEndpoints = (userId) => {
        if (this._customEndpointsUnsubscribe) this._customEndpointsUnsubscribe();
        try {
            const q = query(collection(this.firestore, `users/${userId}/customEndpoints`));
            this._customEndpointsUnsubscribe = onSnapshot(q, (snapshot) => {
                this._customEndpointModels = snapshot.docs.map((docSnap) => {
                    const data = docSnap.data();
                    return {
                        id: `custom-${docSnap.id}`,
                        name: data.displayName || data.modelId,
                        platform: `CUSTOM_${docSnap.id.toUpperCase()}`,
                        model: data.modelId,
                        cleanModelName: data.displayName || data.modelId,
                        structuredToolMode: data.structuredToolMode ?? false,
                        isCustomEndpoint: true,
                        hasThinking: true,
                    };
                });
                this._updatePlatformModelsWithKeys();
                this._resolveActiveBotConfig();
                this._notifyListeners();
            }, () => {
                // Snapshot error — custom endpoints section stays empty, not fatal
                this._customEndpointModels = [];
            });
        } catch {
            this._customEndpointModels = [];
        }
    }

    _clearCustomEndpoints = () => {
        if (this._customEndpointsUnsubscribe) {
            this._customEndpointsUnsubscribe();
            this._customEndpointsUnsubscribe = null;
        }
        this._customEndpointModels = [];
    }

    _handleSettingsChanged = () => {
        this._resolveActiveBotConfig();
        this._notifyListeners();
    }

    _handleKeyStateChanged = () => {
        this._updatePlatformModelsWithKeys();
        this._resolveActiveBotConfig();
        this._notifyListeners();
    }

    // ========== THIS IS THE REFACTORED FUNCTION ==========
    _handleAuthStateChanged = async (authState) => {
        if (!authState?.isInitialized) {
            if (!this._state.isInitialized) {
                this._state.isInitialized = true;
                this._updateLoadingState(false);
            }
            return;
        }
    
        const newUserId = authState.user?.uid;
        const userHasChanged = this._currentUserId !== newUserId;
    
        if (userHasChanged || !this._state.isInitialized) {
            this._updateLoadingState(true);
        }
    
        this._currentUserId = newUserId;
        this._state._userTier = authState?.tier || null;
    
        if (userHasChanged) {
            if (this._keyManagerInstance && authState.user) {
                await this._keyManagerInstance.refreshFromDB().catch(() => {});
            } else if (!authState.user && this._keyManagerInstance?.clearKeys) {
                this._keyManagerInstance.clearKeys();
            }
    
            if (authState.user) {
                this._listenToCustomBots(authState.user.uid);
                this._listenToCustomEndpoints(authState.user.uid);
            } else {
                this._clearCustomBots();
                this._clearCustomEndpoints();
            }
        }
    
        await this._fetchPlatformModels();
        this._updatePlatformModelsWithKeys();
    
        if (userHasChanged) {
            this._setDefaultBotAndModel();
        } else {
            this._resolveActiveBotConfig();
        }
    
        if (!this._state.isInitialized) {
            this._state.isInitialized = true;
        }
        this._updateLoadingState(false);
        this._notifyListeners();
    }
    // =======================================================

    _setDefaultBotAndModel = () => {
        let targetBotId = null;
        let targetModelId = null;
    
        try {
            const storedBotId = localStorage.getItem(LOCAL_STORAGE_LAST_BOT_ID_KEY);
            const storedModelId = localStorage.getItem(LOCAL_STORAGE_LAST_MODEL_ID_KEY);
    
            if (storedBotId && storedModelId) {
                const isStoredModelUsable = this._state.platformModelsWithKeys.some(m => m.id === storedModelId);
                const doesBotExist = !!this.getBotById(storedBotId);
    
                if (isStoredModelUsable && doesBotExist) {
                    targetBotId = storedBotId;
                    targetModelId = storedModelId;
                }
            }
        } catch (e) { /* Silent */ }
    
        if (!targetBotId || !targetModelId) {
            const defaultPromptedBot = this._state.houseBots.find(b => b.id === DEFAULT_PROMPTED_BOT_ID);
            if (defaultPromptedBot) {
                targetBotId = defaultPromptedBot.id;
                const modelToUse = this._pickDefaultModel(this._state.platformModelsWithKeys);
    
                if (modelToUse) {
                    targetModelId = modelToUse.id;
                }
            }
        }
    
        this._state.currentBotId = targetBotId;
        this._state.currentModelId = targetModelId;
    
        try {
            if (targetBotId && targetModelId) {
                localStorage.setItem(LOCAL_STORAGE_LAST_BOT_ID_KEY, targetBotId);
                localStorage.setItem(LOCAL_STORAGE_LAST_MODEL_ID_KEY, targetModelId);
            } else {
                localStorage.removeItem(LOCAL_STORAGE_LAST_BOT_ID_KEY);
                localStorage.removeItem(LOCAL_STORAGE_LAST_MODEL_ID_KEY);
            }
        } catch (e) { /* Silent */ }
    
        this._resolveActiveBotConfig();
    }

    _resolveActiveBotConfig = () => {
        const { currentBotId, currentModelId, allPlatformModels, platformModelsWithKeys } = this._state;

        if (!currentBotId || !currentModelId) {
            if (this._state.activeBotConfig !== null) {
                this._state.activeBotConfig = null;
                this._notifyListeners();
            }
            return;
        }

        const baseBot = this.getBotById(currentBotId);
        const modelDetails = this._getTierFilteredModels(allPlatformModels).find(m => m.id === currentModelId);

        if (!currentBotId || !currentModelId || !baseBot || !modelDetails) {
            if (this._state.activeBotConfig !== null) {
                 this._state.activeBotConfig = null;
                 this._notifyListeners();
            }
            return;
        }

        const uiBotName = baseBot.name;
        const isUsable = platformModelsWithKeys.some(m => m.id === modelDetails.id);

        const newActiveBotConfig = {
            botId: baseBot.id,
            modelId: modelDetails.id,
            uiName: uiBotName,
            description: baseBot.description || modelDetails.description,
            isHouseBot: !!baseBot.isHouseBot,
            isCustomBot: !!baseBot.isCustomBot,
            isVanillaBot: baseBot.id === 'vanillabot',
            isUsable: isUsable,
            serverBotId: baseBot.isCustomBot ? baseBot.id : (baseBot.serverBotId || baseBot.id),
            serverModelString: modelDetails.model,
            serverPlatform: modelDetails.platform.toUpperCase(),
            serverCustomSystemMessage: getBotRuntimeSystemMessage(baseBot),
            serverModelMaxTokens: modelDetails.maxTokens || null,
            rawModelDetails: { ...modelDetails },
            rawBotDetails: { ...baseBot }
        };

        if (JSON.stringify(this._state.activeBotConfig) !== JSON.stringify(newActiveBotConfig)) {
            this._state.activeBotConfig = newActiveBotConfig;
            this._notifyListeners();
        }
    }
    
    // ========== THIS IS THE UPDATED FUNCTION ==========
    initialize = async (authManagerPassed, keyManagerPassed) => {
        if (this._hasInitialized) return;
        this._hasInitialized = true;

        this.firestore = getFirestore();
        this._authManagerInstance = authManagerPassed || defaultAuthManager;
        this._keyManagerInstance = keyManagerPassed || defaultKeyStateManager;

        this._keyManagerUnsubscribe = this._keyManagerInstance.subscribe(this._handleKeyStateChanged);
        
        // This subscription now correctly handles subsequent updates.
        this._authUnsubscribe = this._authManagerInstance.subscribe(this._handleAuthStateChanged);

        // The initial, manual call correctly populates state on first load.
        await this._handleAuthStateChanged(this._authManagerInstance.getState());
    }
    // ===================================================

    subscribe = (listener) => {
        if (typeof listener !== 'function') return () => {};
        this._listeners.add(listener);
        listener(this.getState()); // Immediately send the current state
        return () => this._listeners.delete(listener);
    }

    getState = () => {
        const activeConf = this._state.activeBotConfig;
        const legacyCurrentBot = activeConf ? {
            ...activeConf.rawBotDetails,
            name: activeConf.uiName,
            id: activeConf.botId
        } : null;

        const legacyCurrentModel = activeConf ? {
            ...activeConf.rawModelDetails,
            id: activeConf.modelId,
            platform: activeConf.serverPlatform,
            model: activeConf.serverModelString,
            cleanModelName: activeConf.rawModelDetails?.name || activeConf.serverModelString,
            maxTokens: activeConf.serverModelMaxTokens,
            temperatureRange: activeConf.rawModelDetails?.temperatureRange
        } : null;

        const platformsMap = {};
        const platformsWithKeysArray = this._getAvailablePlatformsSafely();
        for (const p of platformsWithKeysArray) {
            platformsMap[p.toUpperCase()] = true;
        }

        const tierFilteredAllModels = this._getTierFilteredModels([
            ...this._state.allPlatformModels,
            ...this._customEndpointModels,
        ]);

        const vanillaBotDefaultModelId = VANILLA_BOT_CONFIG.defaultModelId ||
                                       this._pickDefaultModel(tierFilteredAllModels)?.id;
        const vanillaBotConfigWithVariations = {
            ...VANILLA_BOT_CONFIG,
            defaultModelId: vanillaBotDefaultModelId,
            variations: [...tierFilteredAllModels.map(m => ({...m}))],
        };

        const houseBotsWithVariations = this._state.houseBots.map(b => {
            const botDefinition = {...b};
            if (b.id === DEFAULT_PROMPTED_BOT_ID) {
                botDefinition.variations = [...tierFilteredAllModels.map(m => ({...m}))];
            }
            return botDefinition;
        });

        const userCustomBotsWithVariations = this._state.userCustomBots.map(cb => ({
            ...cb,
            variations: [...tierFilteredAllModels.map(m => ({...m}))],
        }));

        const allBotsList = [
            ...houseBotsWithVariations,
            ...userCustomBotsWithVariations,
            vanillaBotConfigWithVariations
        ];

        const hasPlatformKeys = this._state.platformModelsWithKeys.length > 0;

        return {
            isInitialized: this._state.isInitialized,
            isLoading: this._state.isLoading,
            platformModelsWithKeys: [...this._state.platformModelsWithKeys.map(m => ({...m}))], 
            allPlatformModels: [...this._state.allPlatformModels.map(m => ({...m})), ...this._customEndpointModels.map(m => ({...m}))], // Return the actual list now (includes custom endpoints)
            houseBots: houseBotsWithVariations,
            userCustomBots: userCustomBotsWithVariations,
            allBots: allBotsList,
            platformsWithActiveKeysMap: platformsMap,
            hasPlatformKeys,
            currentBotId: this._state.currentBotId,
            currentModelId: this._state.currentModelId,
            activeBotConfig: activeConf ? { ...activeConf } : null,
            currentBot: legacyCurrentBot,
            currentModel: legacyCurrentModel,
            hasAllBots: allBotsList.length > 0,
            allBotsLength: allBotsList.length,
            hasAllPlatformModels: this._state.allPlatformModels.length > 0, 
            allPlatformModelsLength: this._state.allPlatformModels.length, 
            hasActiveBotConfig: !!activeConf,
        };
    }

    getCurrentBotContext = () => {
        return this._state.activeBotConfig ? { ...this._state.activeBotConfig } : null;
    }

    getBotById = (botId) => {
        if (!botId) return null;

        const tierFilteredAllModels = this._getTierFilteredModels([
            ...this._state.allPlatformModels,
            ...this._customEndpointModels,
        ]);

        if (botId === 'vanillabot') {
            const defaultModelId = VANILLA_BOT_CONFIG.defaultModelId ||
                                 this._pickDefaultModel(tierFilteredAllModels)?.id;
            return {
                ...VANILLA_BOT_CONFIG,
                defaultModelId: defaultModelId,
                variations: [...tierFilteredAllModels.map(m => ({...m}))]
            };
        }

        const houseBot = this._state.houseBots.find(b => b.id === botId);
        if (houseBot) {
            return {
                ...houseBot,
                variations: houseBot.id === DEFAULT_PROMPTED_BOT_ID ? [...tierFilteredAllModels.map(m => ({...m}))] : houseBot.variations || []
            };
        }

        const customBot = this._state.userCustomBots.find(b => b.id === botId);
        if (customBot) {
            return {
                ...customBot,
                variations: [...tierFilteredAllModels.map(m => ({...m}))]
            };
        }
        return null;
    }

    getAvailableModelsForBot = (botId) => {
        const bot = this.getBotById(botId);
        if (!bot) return [];

        let modelsToList = [];
        if (bot.id === DEFAULT_PROMPTED_BOT_ID || bot.id === 'vanillabot' || bot.isCustomBot) {
            modelsToList = [...this._getTierFilteredModels([
                ...this._state.allPlatformModels,
                ...this._customEndpointModels,
            ])];
        } else if (bot.variations && Array.isArray(bot.variations)) {
            modelsToList = [...bot.variations];
        }

        const platformsWithKeysMap = (this.getState()).platformsWithActiveKeysMap || {};
        return modelsToList.map(m => ({
            ...m,
            name: m.name || m.cleanModelName || m.model,
            isUsable: !!platformsWithKeysMap[String(m.platform).toUpperCase()]
        }));
    }

    setCurrentBotAndModel = (botId, modelId) => {
        const botExists = !!this.getBotById(botId);
        const modelExistsInUsable = this._state.platformModelsWithKeys.some(m => m.id === modelId);
    
        if (!botExists || !modelExistsInUsable) {
            return;
        }
    
        if (this._state.currentBotId !== botId || this._state.currentModelId !== modelId) {
            this._state.currentBotId = botId;
            this._state.currentModelId = modelId;
            try {
                localStorage.setItem(LOCAL_STORAGE_LAST_BOT_ID_KEY, botId);
                localStorage.setItem(LOCAL_STORAGE_LAST_MODEL_ID_KEY, modelId);
            } catch (e) {
                // Silent error handling
            }
            this._resolveActiveBotConfig();
        }
    }

    getAvailableBotsForUI = () => {
        const { allBots, platformsWithActiveKeysMap } = this.getState();
        return allBots.map(bot => {
            let isUsableOverall = false;
            const modelsForThisBot = bot.variations || [];
            isUsableOverall = modelsForThisBot.some(model =>
                !!platformsWithActiveKeysMap[String(model.platform).toUpperCase()]
            );
            let botType = 'unknown';
            if (bot.isHouseBot && bot.id === DEFAULT_PROMPTED_BOT_ID) {
                botType = 'house';
            } else if (bot.isCustomBot) {
                botType = 'custom';
            } else if (bot.id === 'vanillabot') {
                botType = 'vanillabot';
            }
            return { ...bot, type: botType, isUsable: isUsableOverall };
        });
    }

    /* ========================
     * findDefaultBot - Finds the default bot and a suitable model variation.
     * Used for creating a new conversation context when needed.
     * ========================*/
    findDefaultBot = () => {
        const defaultPromptedBot = this.getBotById(DEFAULT_PROMPTED_BOT_ID);
        if (!defaultPromptedBot) {
            return null; // Should not happen if house bots are configured
        }

        const { platformModelsWithKeys } = this._state;

        // If no models are usable (e.g., no keys), return the bot but no variation.
        if (platformModelsWithKeys.length === 0) {
            return { bot: defaultPromptedBot, variation: null };
        }

        const modelToUse = this._pickDefaultModel(platformModelsWithKeys);

        return { bot: defaultPromptedBot, variation: modelToUse };
    }

    saveCustomBot = async (botData) => {
        const currentUser = this._authManagerInstance?.getCurrentUser ? this._authManagerInstance.getCurrentUser() : null;
        if (!currentUser) throw new Error("User not authenticated.");

        if (!botData.name || typeof botData.customSystemMessage !== 'string') {
            throw new Error("Missing required fields for custom bot (name, customSystemMessage).");
        }

        const botId = botData.id || `custom_${currentUser.uid}_${Date.now()}`;
        const botRef = doc(this.firestore, `users/${currentUser.uid}/customBots`, botId);
        const dataToSave = {
            name: botData.name,
            description: botData.description || '',
            customSystemMessage: botData.customSystemMessage,
            createdAt: botData.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        const tierFilteredAllModels = this._getTierFilteredModels(this._state.allPlatformModels);
        const defaultModelIdForCustomBot = VANILLA_BOT_CONFIG.defaultModelId ||
                                           this._pickDefaultModel(tierFilteredAllModels)?.id;

        await setDoc(botRef, dataToSave, { merge: true });
        return {
            ...dataToSave,
            id: botId,
            serverBotId: 'vanillabot',
            isCustomBot: true,
            defaultModelId: defaultModelIdForCustomBot
        };
    }

    deleteCustomBot = async (botId) => {
        const currentUser = this._authManagerInstance?.getCurrentUser ? this._authManagerInstance.getCurrentUser() : null;
        if (!currentUser) throw new Error("User not authenticated.");
        if (!botId) throw new Error("Bot ID is required for deletion.");
        const botRef = doc(this.firestore, `users/${currentUser.uid}/customBots`, botId);
        await deleteDoc(botRef);
        if (this._state.currentBotId === botId) {
            this._setDefaultBotAndModel();
        }
    }

    destroy = () => {
        if (this._authUnsubscribe) this._authUnsubscribe();
        if (this._customBotsUnsubscribe) this._customBotsUnsubscribe();
        if (this._settingsUnsubscribe) this._settingsUnsubscribe();
        if (this._keyManagerUnsubscribe) this._keyManagerUnsubscribe();
        this._listeners.clear();
        this._state = {
            isInitialized: false,
            isLoading: true,
            allPlatformModels: [],
            platformModelsWithKeys: [],
            houseBots: [JSON.parse(JSON.stringify(DEFAULT_PROMPTED_BOT_CONFIG))],
            userCustomBots: [],
            currentBotId: null,
            currentModelId: null,
            activeBotConfig: null,
            _userTier: null,
        };
        this._hasInitialized = false;
        this._currentUserId = null;
    }
}

export const botManager = new BotManagerClass();
export default botManager;
